import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useWebSocketEvent } from '../../context/WebSocketContext';
import { patientApi } from '../../redux/api/patientApi';
import { watchApi } from '../../redux/api/watchApi';
import { emergencyButtonApi } from '../../redux/api/emergencyButtonApi';

// Helper to determine if the patient matches the incoming socket payload
const isPatientMatch = (patient: any, targetPatientId: number | string, targetImei: string) => {
	if (targetPatientId && (patient.patientId === targetPatientId || patient.id === targetPatientId)) return true;
	if (targetImei) {
		if (patient.SmartWatch?.imei === targetImei) return true;
		if (patient.EmergencyButton?.imei === targetImei) return true;
		if (patient.imei === targetImei) return true;
		if (patient.imeiEmergencyButton === targetImei) return true;
	}
	return false;
};

const isOnlineVal = (status: any) => {
	if (typeof status === 'boolean') return status;
	if (typeof status === 'string') return status.toLowerCase() === 'online';
	if (typeof status === 'number') return status === 1;
	return false;
};

export const GlobalSocketListener = () => {
	const dispatch = useDispatch();

	// WebSocket: Actualización de signos vitales
	useWebSocketEvent('VITALS_UPDATE', useCallback((payload: any) => {
		if (!payload) return;
		const { patientId, imei, heartRate, bloodPressure, oxygen, steps, powerPercentage } = payload;
		const timestamp = payload.timestamp || new Date().toISOString();
		
		// 1. Actualizar lista general de pacientes
		dispatch(
			patientApi.util.updateQueryData('getAllPatients', undefined, (draftPatients: any[]) => {
				const patient = draftPatients.find(p => isPatientMatch(p, patientId, imei));
				if (patient) {
					patient.lastUpdate = timestamp;
					if (heartRate !== undefined && heartRate !== null) patient.heartRate = heartRate;
					if (oxygen !== undefined && oxygen !== null) patient.oxygen = oxygen;
					if (bloodPressure !== undefined && bloodPressure !== null) patient.bloodPressure = bloodPressure;
					if (powerPercentage !== undefined && powerPercentage !== null) patient.powerPercentage = powerPercentage;

					if (patient.SmartWatch) {
						if (steps !== undefined) patient.SmartWatch.steps = steps;
						if (powerPercentage !== undefined) patient.SmartWatch.powerPercentage = powerPercentage;
						if (heartRate !== undefined) patient.SmartWatch.lastHeartRate = heartRate;
						if (oxygen !== undefined) patient.SmartWatch.lastOxygen = oxygen;
						if (bloodPressure !== undefined) patient.SmartWatch.lastBloodPressure = bloodPressure;
					}
					if (patient.EmergencyButton) {
						if (steps !== undefined) patient.EmergencyButton.steps = steps;
						if (powerPercentage !== undefined) patient.EmergencyButton.powerPercentage = powerPercentage;
					}
				}
			})
		);

		// 2. Actualizar query individual del paciente (si se encuentra abierta en ProfilePatient)
		if (patientId) {
			dispatch(
				patientApi.util.updateQueryData('getPatient', patientId, (draftPatient: any) => {
					if (draftPatient) {
						draftPatient.lastUpdate = timestamp;
						if (heartRate !== undefined && heartRate !== null) draftPatient.heartRate = heartRate;
						if (oxygen !== undefined && oxygen !== null) draftPatient.oxygen = oxygen;
						if (bloodPressure !== undefined && bloodPressure !== null) draftPatient.bloodPressure = bloodPressure;
						if (powerPercentage !== undefined && powerPercentage !== null) draftPatient.powerPercentage = powerPercentage;

						if (draftPatient.SmartWatch) {
							if (steps !== undefined) draftPatient.SmartWatch.steps = steps;
							if (powerPercentage !== undefined) draftPatient.SmartWatch.powerPercentage = powerPercentage;
							if (heartRate !== undefined) draftPatient.SmartWatch.lastHeartRate = heartRate;
							if (oxygen !== undefined) draftPatient.SmartWatch.lastOxygen = oxygen;
							if (bloodPressure !== undefined) draftPatient.SmartWatch.lastBloodPressure = bloodPressure;
						}
						if (draftPatient.EmergencyButton) {
							if (steps !== undefined) draftPatient.EmergencyButton.steps = steps;
							if (powerPercentage !== undefined) draftPatient.EmergencyButton.powerPercentage = powerPercentage;
						}
					}
				})
			);
		}

		// 3. Actualizar datos en vivo del smartwatch por IMEI
		if (imei) {
			dispatch(
				patientApi.util.updateQueryData('getWatchData', imei, (draftWatchData: any) => {
					if (draftWatchData) {
						if (steps !== undefined) draftWatchData.steps = steps;
						if (powerPercentage !== undefined) draftWatchData.powerPercentage = powerPercentage;
						if (heartRate !== undefined) draftWatchData.lastHeartRate = heartRate;
						if (oxygen !== undefined) draftWatchData.lastOxygen = oxygen;
						if (bloodPressure !== undefined) draftWatchData.lastBloodPressure = bloodPressure;
					}
				})
			);
		}
	}, [dispatch]));

	// WebSocket: Estado del dispositivo (Online/Offline)
	useWebSocketEvent('DEVICE_STATUS_CHANGE', useCallback((payload: any) => {
		if (!payload) return;
		const { imei, status, patientId, type } = payload;
		const isOnline = isOnlineVal(status);
		const statusStr = isOnline ? 'ONLINE' : 'OFFLINE';

		// 1. Actualizar lista general de pacientes
		dispatch(
			patientApi.util.updateQueryData('getAllPatients', undefined, (draftPatients: any[]) => {
				const patient = draftPatients.find(p => isPatientMatch(p, patientId, imei));
				if (patient) {
					const matchWatch = (type === 'SW' || !type) && (patient.SmartWatch?.imei === imei || patient.imei === imei);
					const matchEB = (type === 'EB' || !type) && (patient.EmergencyButton?.imei === imei || patient.imeiEmergencyButton === imei);

					if (matchWatch && patient.SmartWatch) {
						patient.SmartWatch.status = statusStr;
					}
					if (matchEB && patient.EmergencyButton) {
						patient.EmergencyButton.status = statusStr;
					}
				}
			})
		);

		// 2. Actualizar query individual del paciente
		if (patientId) {
			dispatch(
				patientApi.util.updateQueryData('getPatient', patientId, (draftPatient: any) => {
					if (draftPatient) {
						const matchWatch = (type === 'SW' || !type) && (draftPatient.SmartWatch?.imei === imei || draftPatient.imei === imei);
						const matchEB = (type === 'EB' || !type) && (draftPatient.EmergencyButton?.imei === imei || draftPatient.imeiEmergencyButton === imei);

						if (matchWatch && draftPatient.SmartWatch) {
							draftPatient.SmartWatch.status = statusStr;
						}
						if (matchEB && draftPatient.EmergencyButton) {
							draftPatient.EmergencyButton.status = statusStr;
						}
					}
				})
			);
		}

		// 3. Actualizar lista de smartwatches en watchApi
		if (imei && (type === 'SW' || !type)) {
			dispatch(
				watchApi.util.updateQueryData('getAllWatches', undefined, (draftWatches: any[]) => {
					const watch = draftWatches?.find(w => String(w.imei) === String(imei));
					if (watch) {
						watch.status = statusStr.toLowerCase();
						watch.isConnected = isOnline;
					}
				})
			);
		}

		// 4. Actualizar lista de botones de emergencia en emergencyButtonApi
		if (imei && (type === 'EB' || !type)) {
			dispatch(
				emergencyButtonApi.util.updateQueryData('getAllEmergencyButtons', undefined, (draftButtons: any[]) => {
					const btn = draftButtons?.find(b => String(b.imei) === String(imei));
					if (btn) {
						btn.status = statusStr;
						btn.isConnected = isOnline;
					}
				})
			);
		}
	}, [dispatch]));

	// WebSocket: Actualización de última ubicación
	useWebSocketEvent('LOCATION_UPDATE', useCallback((payload: any) => {
		if (!payload) return;
		const { patientId, imei, lat, lng, isGps, battery, steps } = payload;
		const timestamp = payload.timestamp || new Date().toISOString();

		// 1. Actualizar lista general de pacientes
		dispatch(
			patientApi.util.updateQueryData('getAllPatients', undefined, (draftPatients: any[]) => {
				const patient = draftPatients.find(p => isPatientMatch(p, patientId, imei));
				if (patient) {
					patient.lastUpdate = timestamp;
					patient.lastLocation = {
						lat,
						lng,
						isGps: !!isGps,
						timestamp
					};
					if (battery !== undefined) patient.powerPercentage = battery;
					if (patient.SmartWatch) {
						if (battery !== undefined) patient.SmartWatch.powerPercentage = battery;
						if (steps !== undefined) patient.SmartWatch.steps = steps;
					}
					if (patient.EmergencyButton) {
						if (battery !== undefined) patient.EmergencyButton.powerPercentage = battery;
						if (steps !== undefined) patient.EmergencyButton.steps = steps;
					}
				}
			})
		);

		// 2. Actualizar query individual del paciente
		if (patientId) {
			dispatch(
				patientApi.util.updateQueryData('getPatient', patientId, (draftPatient: any) => {
					if (draftPatient) {
						draftPatient.lastUpdate = timestamp;
						draftPatient.lastLocation = {
							lat,
							lng,
							isGps: !!isGps,
							timestamp
						};
						if (battery !== undefined) draftPatient.powerPercentage = battery;
						if (draftPatient.SmartWatch) {
							if (battery !== undefined) draftPatient.SmartWatch.powerPercentage = battery;
							if (steps !== undefined) draftPatient.SmartWatch.steps = steps;
						}
						if (draftPatient.EmergencyButton) {
							if (battery !== undefined) draftPatient.EmergencyButton.powerPercentage = battery;
							if (steps !== undefined) draftPatient.EmergencyButton.steps = steps;
						}
					}
				})
			);
		}
	}, [dispatch]));

	// WebSocket: Reconexión (Fuerza recarga de los pacientes y dispositivos)
	useWebSocketEvent('_RECONNECT_', useCallback(() => {
		dispatch(patientApi.util.invalidateTags(['Patient', 'PatientLocations', 'WatchData', 'EmergencyButtonData']));
		dispatch(watchApi.util.invalidateTags(['Watch']));
		dispatch(emergencyButtonApi.util.invalidateTags(['EmergencyButton']));
	}, [dispatch]));

	return null; // Componente invisible
};
